import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";
import GridTable from "../../../../components/GridTable";
import Grid from "@mui/material/Grid";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import LockResetIcon from "@mui/icons-material/LockReset";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import bcrypt from "bcryptjs";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "@mui/material/TextField";
import Dialog from "../../../../components/Dialog";
import Loader from "../../../../components/Loader";
import Button from "../../../../components/Button";
import Dropdown from "../../../../components/Dropdown";
import ActionMenu from "../../../../components/ActionMenu";
import { successMessage, errorMessage } from "../../../../components/Toaster";
import {
  getUsers,
  getManagers,
  createUser,
  updateUser,
  deleteUser,
  updateUserStatus,
  getRoleNames,
} from "./api";
import { resetPassword } from "../../../Login/api";
import { getErrorMessage } from "../../../../utils/error";

const User = () => {
  const location = useLocation();
  const { company_id, yard_id } = useParams();
  const company_name = location.state?.company_name || "Company";
  const yard_name = location.state?.yard_name || "Yard";
  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [roleNames, setRoleNames] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [loading, setLoading] = useState({
    create: false,
    fetch: false,
    delete: false,
    update: false,
  });

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First Name is required"),
    userName: Yup.string().required("User Name is required"),
    role: Yup.string().required("Role is required"),
    password: Yup.string().when([], {
      is: () => !editUser,
      then: (schema) => schema.required("Password is required"),
    }),
    phoneNumber: Yup.string().when("email", {
      is: (email) => Boolean(email),
      then: (schema) => schema.nullable(),
      otherwise: (schema) =>
        schema
          .required("Either Email or Phone Number is required")
          .matches(/^\d{10}$/, "Phone Number must be exactly 10 digits"),
    }),
    email: Yup.string().email("Invalid email format").nullable(),
  });

  const columns = [
    {
      field: "first_name",
      headerName: "First Name",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "user_name",
      headerName: "User Name",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "role_name",
      headerName: "Role",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "contact_info",
      headerName: "Phone/Email",
      headerClassName: "table-header",
      flex: 1,
      renderCell: (params) => {
        const phoneNumber = params.row?.phone_number;
        const email = params.row?.email;
        return <div>{phoneNumber || email || "N/A"}</div>;
      },
    },
    {
      field: "is_active",
      headerName: "Is Active",
      headerClassName: "table-header",
      flex: 1,
      renderCell: (params) => {
        const isActive = params.row?.is_active;
        const userId = params.row?.user_id;
        return (
          <Button
            label={isActive ? "Active" : "Inactive"}
            color={isActive ? "success" : "error"}
            variant="contained"
            size="small"
            onClick={() => UpdateStatus(userId, isActive)}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      headerClassName: "table-header",
      renderCell: (params) => {
        return (
          <ActionMenu
            params={params}
            actions={[
              {
                icon: <EditIcon color="primary" />,
                tooltip: "Edit",
                onClick: handleEdit,
              },
              {
                icon: <DeleteIcon color="error" />,
                tooltip: "Delete",
                onClick: handleDelete,
              },
              {
                icon: <LockResetIcon color="primary" />,
                tooltip: "Reset Password",
                onClick: handleResetPassword,
              },
            ]}
          />
        );
      },
      sortable: false,
      filterable: false,
      flex: 1,
    },
  ];

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading((prev) => ({ ...prev, fetch: true }));
      try {
        const usersData = await getUsers(company_id || "", yard_id || "");
        if (usersData.status && usersData.data) {
          setUsers(usersData.data);
        } else {
          errorMessage(usersData.message || "Failed to fetch users.");
        }
      } catch (error) {
        errorMessage(getErrorMessage(error) || "Error fetching users");
      } finally {
        setLoading((prev) => ({ ...prev, fetch: false }));
      }
    };

    fetchUsers();
  }, [company_id, yard_id]);

  const handleEdit = (params) => {
    setEditUser(params.row);
    openDialog(params.row);
  };

  const handleDelete = (params) => {
    setItemToDelete(params);
    setDeleteOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setItemToDelete(null);
  };

  const UpdateStatus = async (userId, currentStatus) => {
    setLoading((prev) => ({ ...prev, fetch: true }));
    try {
      const updatedData = {
        user_id: userId,
        is_active: !currentStatus,
      };
      const response = await updateUserStatus(updatedData);
      if (response.status) {
        successMessage(response.message || "User status updated.");
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.user_id === userId
              ? { ...user, is_active: !currentStatus }
              : user,
          ),
        );
      } else {
        errorMessage(response.message || "Failed to update user status.");
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      const message = getErrorMessage(error);
      errorMessage(message || "An unexpected error occurred.");
    } finally {
      setLoading((prev) => ({ ...prev, fetch: false }));
    }
  };

  const openDialog = async () => {
    setDialogOpen(true);
    setLoading((prev) => ({ ...prev, fetch: true }));
    try {
      const [roleNamesResponse, managersResponse] = await Promise.all([
        getRoleNames(),
        getManagers(company_id, yard_id),
      ]);

      if (roleNamesResponse.status && roleNamesResponse?.data) {
        setRoleNames(roleNamesResponse.data);
      } else {
        errorMessage(
          roleNamesResponse.message || "Failed to fetch role names.",
        );
      }

      if (managersResponse.status && managersResponse?.data) {
        setManagers(managersResponse.data);
      } else {
        errorMessage(managersResponse.message || "Failed to fetch managers.");
      }
    } catch (error) {
      console.error("Error fetching role names:", error);
      const message = getErrorMessage(error);
      errorMessage(message || "Error fetching role names");
    } finally {
      setLoading((prev) => ({ ...prev, fetch: false }));
    }
  };

  const closeDialog = (resetForm) => {
    resetForm();
    setDialogOpen(false);
    setEditUser(null);
  };

  const confirmDialog = async (values, { resetForm }) => {
    let success = false;

    if (editUser) {
      success = await handleUpdate(values);
    } else {
      success = await handleCreate(values);
    }

    if (success) {
      closeDialog(resetForm);
    }
  };

  const handleCreate = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    try {
      const hashedPassword = await bcrypt.hash(values.password, 10);
      const newUser = {
        first_name: values.firstName.trim(),
        last_name: values?.lastName ? values.lastName.trim() : null,
        user_name: values.userName.trim(),
        password: hashedPassword,
        company_id: company_id || "",
        yard_id: yard_id || "",
        role_name: values?.role,
        manager_id: values?.managerId,
        is_password_set: values.isPasswordSet,
        ...(values?.phoneNumber?.trim() && {
          phone_number: values.phoneNumber.trim(),
        }),
        ...(values?.email?.trim() && { email: values.email.trim() }),
      };

      const response = await createUser(newUser);
      if (response?.status) {
        setUsers((prevUsers) => {
          const currentUsers = Array.isArray(prevUsers) ? prevUsers : [];
          return [
            ...currentUsers,
            {
              user_id: response?.data?.user_id,
              first_name: values.firstName,
              last_name: values?.lastName ? values.lastName.trim() : null,
              user_name: values.userName,
              phone_number: values.phoneNumber,
              email: values.email,
              company_id: company_id || "",
              yard_id: yard_id || "",
              role_name: values?.role,
              manager_id: values?.managerId,
              is_active: "TRUE",
              is_password_set: values.isPasswordSet,
            },
          ];
        });
        successMessage(response.message || "User created successfully!");
        return true;
      } else {
        errorMessage(
          response?.message || "Failed to create user. Please try again.",
        );
        return false;
      }
    } catch (error) {
      console.error("Error creating user:", error);
      const message = getErrorMessage(error);
      errorMessage(
        message || "An unexpected error occurred. Please try again.",
      );
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const handleUpdate = async (values) => {
    setLoading((prev) => ({ ...prev, update: true }));
    try {
      const updatedUserData = {
        user_id: editUser?.user_id || "",
        first_name: values.firstName.trim(),
        last_name: values?.lastName ? values.lastName.trim() : null,
        user_name: values.userName.trim(),
        company_id: company_id || "",
        yard_id: yard_id || "",
        manager_id: values?.managerId,
        role_name: values?.role,
        is_password_set: values.isPasswordSet,
        ...(values?.phoneNumber?.trim() && {
          phone_number: values.phoneNumber.trim(),
        }),
        ...(values?.email?.trim() && { email: values.email.trim() }),
      };
      const response = await updateUser(updatedUserData);
      if (response.status) {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.user_id === updatedUserData.user_id
              ? {
                  ...user,
                  ...updatedUserData,
                }
              : user,
          ),
        );
        successMessage(response.message || "User updated successfully!");
        return true;
      } else {
        errorMessage(
          response.message || "Failed to update user. Please try again.",
        );
        return false;
      }
    } catch (error) {
      console.error("Error updating user:", error);
      const message = getErrorMessage(error);
      errorMessage(
        message || "An unexpected error occurred. Please try again.",
      );
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, update: false }));
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading((prev) => ({ ...prev, delete: true }));

    if (itemToDelete) {
      try {
        const response = await deleteUser(itemToDelete.row.user_id);

        if (response.status) {
          setUsers((prevUsers) =>
            prevUsers.filter(
              (user) => user.user_id !== itemToDelete.row.user_id,
            ),
          );
          successMessage(response.message || "User deleted successfully!");
        } else {
          errorMessage(
            response.message || "Failed to delete user. Please try again.",
          );
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        const message = getErrorMessage(error);
        errorMessage(
          message || "An unexpected error occurred. Please try again.",
        );
      } finally {
        setLoading((prev) => ({ ...prev, delete: false }));
        setDeleteOpen(false);
      }
    }
  };

  const handleResetPassword = async (params) => {
    setLoading((prev) => ({ ...prev, fetch: true }));
    try {
      const hashedPassword = await bcrypt.hash("test@123", 10);
      const response = await resetPassword({
        user_id: params?.row?.user_id,
        password: hashedPassword,
      });
      successMessage(response.message);
    } catch (error) {
      const message = getErrorMessage(error);
      errorMessage(message || "Reset Password failed.");
    }
    setLoading((prev) => ({ ...prev, fetch: false }));
  };

  return (
    <>
      {loading.fetch ? (
        <Loader color="primary" size={40} />
      ) : (
        <Box style={{ width: "100%", height: "50%" }}>
          <main className="m-a-50">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "30px",
              }}
            >
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                style={{ fontSize: "18px", fontWeight: "bold" }}
              >
                <Link color="primary" to="/company">
                  Company
                </Link>
                <Link color="primary" to={`/company`}>
                  {company_name}
                </Link>
                <Link color="primary" to={`/company/${company_id}/yards`} state={{ company_name: company_name, yard_name: yard_name }}>
                  Yard
                </Link>
                <Link color="primary" to={`/company/${company_id}/yards`} state={{ company_name: company_name, yard_name: yard_name }}>
                  {yard_name}
                </Link>
                <Link
                  color="primary"
                  to={`/company/${company_id}/yards/${yard_id}/users`}
                  state={{ company_name: company_name, yard_name: yard_name }}
                >
                  Users
                </Link>
              </Breadcrumbs>
            </div>
            <div className="d-flex justify-end">
              <div className="d-flex justify-between w-100">
                <TextField
                  className="w-25"
                  label="Search"
                  variant="outlined"
                  value={searchQuery}
                  size="small"
                  startIcon={<SearchIcon />}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button
                  label="Add"
                  variant="contained"
                  color="primary"
                  onClick={() => openDialog()}
                />
              </div>
            </div>
            <div
              style={{ height: "100%", width: "100%" }}
              className="ag-theme-alpine m-t-20"
            >
              <GridTable
                rows={users}
                columns={columns}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                getRowId={(row) => row.user_name}
                density="standard"
              />
            </div>
            <Formik
              initialValues={{
                firstName: editUser ? editUser.first_name : "",
                lastName: editUser ? editUser.last_name : null,
                userName: editUser ? editUser.user_name : "",
                phoneNumber: editUser ? editUser.phone_number : null,
                email: editUser ? editUser.email : null,
                password: editUser ? "" : "",
                role: editUser ? editUser.role_name : "",
                isPasswordSet: editUser ? editUser.is_password_set : false,
                managerId: editUser ? editUser.manager_id : null,
              }}
              validationSchema={validationSchema}
              onSubmit={confirmDialog}
              enableReinitialize
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleSubmit,
                resetForm,
                setFieldValue,
              }) => (
                <>
                  <Dialog
                    open={isDialogOpen}
                    onClose={() => closeDialog(resetForm)}
                    onConfirm={handleSubmit}
                    title={
                      !loading.update && !loading.create
                        ? editUser
                          ? "Edit User"
                          : "Add New User"
                        : ""
                    }
                    confirmButtonText={
                      !loading.update && !loading.create
                        ? editUser
                          ? "Update"
                          : "Add"
                        : ""
                    }
                    cancelButtonText={
                      !loading.update && !loading.create ? "Cancel" : ""
                    }
                    icon={<PersonAddIcon />}
                    message={
                      <Box padding={4}>
                        {loading.create ? (
                          <Loader color="primary" size={40} />
                        ) : (
                          <Form>
                            <Grid container spacing={2}>
                              <Grid size={4}>
                                <TextField
                                  id="firstName"
                                  name="firstName"
                                  label="First Name"
                                  required
                                  value={values.firstName}
                                  onChange={handleChange}
                                  error={
                                    touched.firstName &&
                                    Boolean(errors.firstName)
                                  }
                                  helperText={
                                    touched.firstName && errors.firstName
                                  }
                                />
                              </Grid>
                              <Grid size={4}>
                                <TextField
                                  id="lastName"
                                  name="lastName"
                                  label="Last Name"
                                  value={values.lastName}
                                  onChange={handleChange}
                                  error={
                                    touched.lastName && Boolean(errors.lastName)
                                  }
                                  helperText={
                                    touched.lastName && errors.lastName
                                  }
                                />
                              </Grid>
                              <Grid size={4}>
                                <TextField
                                  id="userName"
                                  name="userName"
                                  label="User Name"
                                  required
                                  value={values.userName}
                                  onChange={handleChange}
                                  error={
                                    touched.userName && Boolean(errors.userName)
                                  }
                                  helperText={
                                    touched.userName && errors.userName
                                  }
                                />
                              </Grid>
                              {!editUser && (
                                <Grid size={4}>
                                  <TextField
                                    id="password"
                                    name="password"
                                    label="Password"
                                    type="password"
                                    value={values.password}
                                    onChange={handleChange}
                                    error={
                                      touched.password &&
                                      Boolean(errors.password)
                                    }
                                    helperText={
                                      touched.password && errors.password
                                    }
                                  />
                                </Grid>
                              )}
                              <Grid size={4}>
                                <TextField
                                  id="phoneNumber"
                                  name="phoneNumber"
                                  label="Phone Number"
                                  value={values.phoneNumber ?? ""}
                                  onChange={handleChange}
                                  error={
                                    touched.phoneNumber &&
                                    Boolean(errors.phoneNumber)
                                  }
                                  helperText={
                                    touched.phoneNumber && errors.phoneNumber
                                  }
                                />
                              </Grid>
                              <Grid size={4}>
                                <TextField
                                  id="email"
                                  name="email"
                                  label="Email"
                                  value={values.email ?? ""}
                                  onChange={handleChange}
                                  error={touched.email && Boolean(errors.email)}
                                  helperText={touched.email && errors.email}
                                />
                              </Grid>
                              <Grid size={4}>
                                <Dropdown
                                  options={managers.map((manager) => ({
                                    id: manager.user_id ?? "",
                                    name: `${manager.first_name} ${manager?.last_name || ""}`,
                                  }))}
                                  value={values.managerId ?? ""}
                                  onChange={(id) =>
                                    setFieldValue("managerId", id)
                                  }
                                  label="Manager"
                                  error={Boolean(
                                    touched.managerId && errors.managerId,
                                  )}
                                  helperText={
                                    touched.managerId && errors.managerId
                                      ? String(errors.managerId)
                                      : undefined
                                  }
                                />
                              </Grid>
                              <Grid size={4}>
                                <Dropdown
                                  options={roleNames}
                                  value={values.role ?? ""}
                                  onChange={(value) =>
                                    setFieldValue("role", value)
                                  }
                                  label="Role Name"
                                  width="200px"
                                  required
                                  error={Boolean(touched.role && errors.role)}
                                  helperText={
                                    touched.role && errors.role
                                      ? String(errors.role)
                                      : undefined
                                  }
                                />
                              </Grid>
                              <Grid size={4}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      id="isPasswordSet"
                                      name="isPasswordSet"
                                      checked={values.isPasswordSet}
                                      onChange={handleChange}
                                      color="primary"
                                    />
                                  }
                                  label="Set Final Password?"
                                />
                                {touched.isPasswordSet &&
                                  errors.isPasswordSet && (
                                    <FormHelperText error>
                                      {errors.isPasswordSet}
                                    </FormHelperText>
                                  )}
                              </Grid>
                            </Grid>
                          </Form>
                        )}
                      </Box>
                    }
                  />
                </>
              )}
            </Formik>
            <Dialog
              open={deleteOpen}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              title={!loading.delete ? "Delete User" : ""}
              confirmButtonText={!loading.delete ? "Confirm" : ""}
              cancelButtonText={!loading.delete ? "Cancel" : ""}
              icon={<DeleteIcon />}
              message={
                <Box padding={2}>
                  {loading.delete ? (
                    <Loader color="primary" size={40} />
                  ) : (
                    "Are you sure you want to delete this user?"
                  )}
                </Box>
              }
            />
          </main>
        </Box>
      )}
    </>
  );
};

export default User;
